import { Button } from '@ui/shared/Button';
import { MultiFactorInfo } from 'firebase/auth';
import * as React from 'react';
import { Row } from '../../../layout/Row';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import { LoginState } from '../Login';
import { useConnectionStatus } from './ConnectionStatusWrapper';

/**
 * Simple email validation - checks for basic email format
 */
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

export interface ILoginFooterProps {
  loginState: LoginState;
  email: string;
  password: string;
  verificationCode: string;
  resendCooldown: number;
  resetSent: boolean;
  selectedFactor: MultiFactorInfo | null;
  selectedProvider: string | null;
  selectedSSOMethod: string | null;
  selectedOrganization: string | null;
  selectedTenant: string | null;
  onBack: () => void;
  onResendCode: () => Promise<void>;
  setResendCooldown: (seconds: number) => void;
  setShowResendSuccess: (show: boolean) => void;
}

export const LoginFooter = React.memo(
  (props: ILoginFooterProps): React.ReactElement => {
    const {
      loginState,
      email,
      password,
      verificationCode,
      resendCooldown,
      resetSent,
      selectedFactor,
      selectedProvider,
      selectedSSOMethod,
      selectedOrganization,
      selectedTenant,
      onBack,
      onResendCode,
      setResendCooldown,
      setShowResendSuccess,
    } = props;

    // Get connection status from context
    const { isOnline } = useConnectionStatus();

    const handleResendCode = React.useCallback(async (): Promise<void> => {
      setShowResendSuccess(false);
      await onResendCode();
      setResendCooldown(30);
      setShowResendSuccess(true);
    }, [onResendCode, setResendCooldown, setShowResendSuccess]);

    const renderActionButton = (): React.ReactElement | null => {
      switch (loginState) {
        case LoginState.IDENTIFY: {
          const isEmailValid = isValidEmail(email);
          return (
            <Button
              icon='email'
              title='Submit Email'
              id='login-button'
              aria-disabled={!isEmailValid || !isOnline}
              style={{ whiteSpace: 'nowrap' }}
              disabled={!isEmailValid || !isOnline}
              type='submit'
            />
          );
        }

        case LoginState.SELECT_TENANT:
          return (
            <Button
              icon='arrow_forward'
              title='Continue'
              id='login-button'
              aria-disabled={!selectedTenant || !isOnline}
              style={{ whiteSpace: 'nowrap' }}
              disabled={!selectedTenant || !isOnline}
              type='submit'
            />
          );

        case LoginState.AUTHENTICATE:
          return (
            <Button
              icon='lock'
              title='Submit Password'
              id='login-button'
              aria-disabled={password.length === 0 || !isOnline}
              style={{ whiteSpace: 'nowrap' }}
              disabled={password.length === 0 || !isOnline}
              type='submit'
            />
          );

        case LoginState.FORGOT_PASSWORD:
          // Don't show button if email was already sent
          if (resetSent) {
            return null;
          }
          return (
            <Button
              icon='send'
              title='Send Reset Link'
              id='login-button'
              aria-disabled={!isOnline}
              style={{ whiteSpace: 'nowrap' }}
              disabled={!isOnline}
              type='submit'
            />
          );

        case LoginState.SECOND_FACTOR_REQUIRED:
          return (
            <Button
              icon='send'
              title='Send Code'
              id='login-button'
              aria-disabled={Boolean(!selectedFactor) || !isOnline}
              style={{ whiteSpace: 'nowrap' }}
              disabled={Boolean(!selectedFactor) || !isOnline}
              type='submit'
            />
          );

        case LoginState.SECOND_FACTOR_VERIFICATION: {
          const isPhoneFactor = selectedFactor?.factorId === 'phone';
          const resendButtonText =
            resendCooldown > 0
              ? `Resend Code (${resendCooldown}s)`
              : 'Resend Code';

          return (
            <React.Fragment>
              <Button
                icon='verified'
                title='Verify Code'
                id='login-button'
                aria-disabled={verificationCode.length < 6 || !isOnline}
                style={{ whiteSpace: 'nowrap' }}
                disabled={verificationCode.length < 6 || !isOnline}
                type='submit'
              />
              {isPhoneFactor && (
                <Button
                  icon='refresh'
                  title={resendButtonText}
                  type='button'
                  style={{ whiteSpace: 'nowrap', marginRight: '10px' }}
                  onClick={handleResendCode}
                  disabled={!isOnline || resendCooldown > 0}
                />
              )}
            </React.Fragment>
          );
        }

        case LoginState.SELECT_PROVIDER:
          return (
            <Button
              icon='arrow_forward'
              title='Continue'
              id='login-button'
              aria-disabled={!selectedProvider || !isOnline}
              style={{ whiteSpace: 'nowrap' }}
              disabled={!selectedProvider || !isOnline}
              type='submit'
            />
          );

        case LoginState.SSO_AUTH:
          return (
            <Button
              icon='login'
              title='Sign in with SSO'
              id='login-button'
              aria-disabled={!selectedSSOMethod || !isOnline}
              style={{ whiteSpace: 'nowrap' }}
              disabled={!selectedSSOMethod || !isOnline}
              type='submit'
            />
          );

        case LoginState.SELECT_ORGANIZATION:
          return (
            <Button
              icon='arrow_forward'
              title='Continue'
              id='login-button'
              aria-disabled={!selectedOrganization || !isOnline}
              style={{ whiteSpace: 'nowrap' }}
              disabled={!selectedOrganization || !isOnline}
              type='submit'
            />
          );

        default:
          return null;
      }
    };

    return (
      <CardFooter surface={8} style={{ flexDirection: 'row-reverse' }}>
        <CardFooterLeft style={{ order: 2 }}>
          {loginState !== LoginState.IDENTIFY && (
            <Row>
              <Button
                icon='arrow_back'
                title='Back'
                type='button'
                style={{ whiteSpace: 'nowrap' }}
                onClick={onBack}
              />
            </Row>
          )}
        </CardFooterLeft>
        <CardFooterRight style={{ order: 1, flexDirection: 'row-reverse' }}>
          {renderActionButton()}
        </CardFooterRight>
      </CardFooter>
    );
  },
  (
    prevProps: Readonly<ILoginFooterProps>,
    nextProps: Readonly<ILoginFooterProps>
  ): boolean => {
    // Only re-render if relevant props change
    return (
      prevProps.loginState === nextProps.loginState &&
      prevProps.email === nextProps.email &&
      prevProps.password === nextProps.password &&
      prevProps.verificationCode === nextProps.verificationCode &&
      prevProps.resendCooldown === nextProps.resendCooldown &&
      prevProps.resetSent === nextProps.resetSent &&
      prevProps.selectedFactor === nextProps.selectedFactor &&
      prevProps.selectedProvider === nextProps.selectedProvider &&
      prevProps.selectedSSOMethod === nextProps.selectedSSOMethod &&
      prevProps.selectedOrganization === nextProps.selectedOrganization &&
      prevProps.selectedTenant === nextProps.selectedTenant &&
      prevProps.onBack === nextProps.onBack &&
      prevProps.onResendCode === nextProps.onResendCode &&
      prevProps.setResendCooldown === nextProps.setResendCooldown &&
      prevProps.setShowResendSuccess === nextProps.setShowResendSuccess
    );
  }
);

LoginFooter.displayName = 'LoginFooter';
